import React, { useEffect, useState } from 'react';
import {
  DIALOG_TYPES,
  useGlobalDialogContext,
} from 'App/components/Layouts/Dashboard/dialog/GlobalDialog';
import { ETwilioCallbackSmsStatus } from 'store/request/interfaces';
import requestService from 'store/request/service';
import { ConfirmRejectionDialog } from './index';
import { DocumentRejectionReasonEnum } from '../../../../common/document/enums/document-rejection-reason.enum';
import { twilioUndeliveredSmsError } from '../../../../integrations/twilio/error/constants';
import { useWebSocketClientMessageSubscription } from 'contexts/webSocketClient/useWebSocketClientSubscription';
import { WebSocketClientMessageTypes } from 'contexts/webSocketClient/messageTypes';
import { useLogsManager } from 'integrations/logs';

export const ConfirmRejectionGlobalDialog = ({
  reason,
  document,
  candidateFullName,
  manager,
  companyName,
  link,
  request,
}: any) => {
  const { hideDialog, showDialog } = useGlobalDialogContext();
  const [lastUsedText, setLastUsedText] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [smsDelivered, setSmsDelivered] = useState(false);
  const [driverSmsStatus, setDriverSmsStatus] = useState<any>();
  const logs = useLogsManager();

  const smsMessageStatusSubscription = useWebSocketClientMessageSubscription(
    WebSocketClientMessageTypes.SMS_MESSAGE_STATUS_UPDATED,
    300 * 1000
  );

  const [rejectDocumentRequest] =
    requestService.endpoints.rejectDocumentRequest.useMutation();

  const [sendDriverRequestSms, { error: errorSendingDriverRequestSms }] =
    requestService.endpoints.sendDriverRequestSms.useMutation();

  const [
    sendDriverRequestSmsToNewNumber,
    { error: errorSendingDriverRequestSmsToNewNumber },
  ] = requestService.endpoints.sendDriverRequestSmsToNewNumber.useMutation();

  const handleDialogToggle = () => {
    hideDialog();
  };

  useEffect(() => {
    if (
      errorSendingDriverRequestSms ||
      errorSendingDriverRequestSmsToNewNumber
    ) {
      setLoading(false);
      showDialog(DIALOG_TYPES.MESSAGE_DELIVERY_ERROR_DIALOG, {
        request,
        lastUsedText,
        error:
          // @ts-ignore
          errorSendingDriverRequestSms?.data ||
          // @ts-ignore
          errorSendingDriverRequestSmsToNewNumber?.data,
      });
    }
  }, [errorSendingDriverRequestSms, errorSendingDriverRequestSmsToNewNumber]);

  useEffect(() => {
    if (driverSmsStatus?.status === ETwilioCallbackSmsStatus.delivered) {
      setSmsDelivered(true);
    }
    if (
      driverSmsStatus?.status &&
      [
        ETwilioCallbackSmsStatus.undelivered,
        ETwilioCallbackSmsStatus.failed,
      ].includes(driverSmsStatus.status as ETwilioCallbackSmsStatus)
    ) {
      setLoading(false);
      showDialog(DIALOG_TYPES.MESSAGE_DELIVERY_ERROR_DIALOG, {
        request,
        lastUsedText,
        error: {
          ...twilioUndeliveredSmsError,
          code: driverSmsStatus?.code || 0,
        },
      });
    }
  }, [driverSmsStatus?.status]);

  const handleSendRejectionMessage = (removeDocumentFromRequest?: boolean) => {
    if (reason && request?.id) {
      return rejectDocumentRequest({
        documentRequestId: request.id,
        candidateDocumentId: document.candidateDocumentId,
        removeDocumentFromRequest,
        ...reason,
      });
    }
  };

  const handleSendMessage = ({
    text,
    newNumber,
    removeDocumentFromRequest,
  }: {
    text: string;
    newNumber?: string;
    removeDocumentFromRequest?: boolean;
  }) => {
    setLastUsedText(text);
    handleSendRejectionMessage(removeDocumentFromRequest);

    setLoading(true);
    if (!newNumber) {
      sendDriverRequestSms({
        documentRequestId: request.id,
        data: {
          text,
        },
      });
    } else {
      sendDriverRequestSmsToNewNumber({
        documentRequestId: request.id,
        data: {
          text,
          number: newNumber,
        },
      });
    }

    smsMessageStatusSubscription.subscribe(
      (message: any) => {
        setDriverSmsStatus(message);
      },
      () => {
        setLoading(false);
        showDialog(DIALOG_TYPES.MESSAGE_DELIVERY_ERROR_DIALOG, {
          request,
          lastUsedText,
          error: twilioUndeliveredSmsError,
        });
        logs.logError(
          'Error subscribing to DIALOG_TYPES.MESSAGE_DELIVERY_ERROR_DIALOG',
          {
            requestId: request.id,
          }
        );
      }
    );
  };

  return (
    <ConfirmRejectionDialog
      open={true}
      setOpen={() => {
        handleDialogToggle();
      }}
      isLoading={isLoading}
      isDelivered={smsDelivered}
      candidateFullName={candidateFullName}
      rejectionReason={
        reason?.rejectionReason || DocumentRejectionReasonEnum.OTHER
      }
      rejectionDetails={reason?.rejectionDetails || ''}
      companyName={companyName}
      manager={manager}
      documentType={document.documentType}
      link={link}
      onSendMessage={handleSendMessage}
    />
  );
};
